import * as React from "react";

/**
 * Recursively clones children and passes shared props to specified components
 * This is used by AlignUI compound components to share state between Root and sub-components
 */
export function recursiveCloneChildren(
  children: React.ReactElement | React.ReactElement[],
  sharedProps: Record<string, unknown>,
  targetDisplayNames: string[],
  uniqueId: string,
  asChild?: boolean
): React.ReactNode {
  return React.Children.map(children, (child) => {
    if (!React.isValidElement(child)) {
      return child;
    }

    const childElement = child as React.ReactElement<
      Record<string, unknown> & { children?: React.ReactNode }
    >;

    // Get the display name of the child component
    const displayName =
      (childElement.type as React.ComponentType)?.displayName ||
      (childElement.type as React.ComponentType)?.name ||
      "";

    // Check if this child should receive the shared props
    const shouldClone = targetDisplayNames.includes(displayName);

    // If asChild is true and this is the first child, we need to handle it differently
    if (asChild && React.Children.count(children) === 1) {
      const grandChildren = childElement.props.children;

      if (grandChildren) {
        return React.cloneElement(childElement, {
          ...childElement.props,
          children: recursiveCloneChildren(
            grandChildren as React.ReactElement[],
            sharedProps,
            targetDisplayNames,
            uniqueId,
            false
          ),
        });
      }

      return child;
    }

    // Clone the child with shared props if it's a target component
    if (shouldClone) {
      return React.cloneElement(childElement, {
        ...sharedProps,
        ...childElement.props,
        key: childElement.key || `${uniqueId}-${displayName}`,
      });
    }

    // If the child has children, recursively process them
    if (childElement.props.children) {
      return React.cloneElement(childElement, {
        ...childElement.props,
        children: recursiveCloneChildren(
          childElement.props.children as React.ReactElement[],
          sharedProps,
          targetDisplayNames,
          uniqueId,
          false
        ),
      });
    }

    return child;
  });
}
